import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const VerifiedReviews = ({ productId }) => {
  const [data, setData] = useState({ reviews: [], stats: { count: 0, average: 0, distribution: {} } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!productId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/product/${productId}`);
        setData(data);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [productId]);

  const { reviews, stats } = data;

  if (loading || stats.count === 0) return null;

  return (
    <section className="border-t border-white/10 pt-16 mt-16" data-testid="verified-reviews-section">
      <div className="flex items-baseline justify-between gap-6 mb-10 flex-wrap">
        <div>
          <p className="label-caps text-[color:var(--gold)] text-[11px] tracking-[0.3em] mb-3 flex items-center gap-2">
            <ShieldCheck size={14} /> Avis clients vérifiés
          </p>
          <h3 className="font-serif text-3xl md:text-4xl">
            {stats.average.toFixed(1)}<span className="text-white/40 text-2xl"> / 5</span>
          </h3>
          <div className="flex items-center gap-2 mt-2">
            <div className="flex">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} size={16} className={n <= Math.round(stats.average) ? "text-[color:var(--gold)] fill-[color:var(--gold)]" : "text-white/20"} strokeWidth={1.5} />
              ))}
            </div>
            <span className="text-white/50 text-sm">· {stats.count} avis</span>
          </div>
        </div>

        {/* Distribution bars */}
        <div className="min-w-[220px]" data-testid="rating-distribution">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = stats.distribution?.[String(n)] || 0;
            const pct = stats.count ? (c / stats.count) * 100 : 0;
            return (
              <div key={n} className="flex items-center gap-3 text-xs mb-1">
                <span className="text-white/40 w-4">{n}★</span>
                <div className="flex-1 h-1.5 bg-white/5 relative">
                  <div className="absolute inset-y-0 left-0 bg-[color:var(--gold)]" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-white/40 w-6 text-right">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="space-y-6">
        {reviews.map((r) => (
          <article key={r.id} className="border border-white/10 p-6 hover:border-white/20 transition-colors" data-testid={`review-${r.id.slice(0,8)}`}>
            <header className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="flex">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} size={14} className={n <= r.rating ? "text-[color:var(--gold)] fill-[color:var(--gold)]" : "text-white/20"} strokeWidth={1.5} />
                ))}
              </div>
              <span className="label-caps text-[10px] text-white/50 tracking-[0.2em]">{r.customer_name}</span>
              <span className="inline-flex items-center gap-1 label-caps text-[9px] text-[color:var(--gold)] border border-[color:var(--gold)]/50 px-2 py-0.5 tracking-[0.2em]">
                <ShieldCheck size={10} /> Achat vérifié
              </span>
              {r.submitted_at && (
                <span className="text-white/30 text-[10px]">{new Date(r.submitted_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</span>
              )}
            </header>
            {r.title && <h4 className="font-serif text-xl mb-2">{r.title}</h4>}
            <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">{r.body}</p>
            {r.photos && r.photos.length > 0 && (
              <div className="flex gap-2 mt-4 flex-wrap">
                {r.photos.map((u) => (
                  <a key={u} href={`${BASE}${u}`} target="_blank" rel="noreferrer" className="w-24 h-24 block border border-white/15 hover:border-[color:var(--gold)] transition-colors">
                    <img src={`${BASE}${u}`} alt="Photo client" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default VerifiedReviews;
